import React from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import ProductSelect from '../../features/invoices/components/ProductSelect';

/**
 * DocumentProductsTable Component
 * Displays product table with add, edit quantity/price, and remove functionality
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentProductsTable = ({
  products = [],
  onProductAdd,
  onProductRemove,
  onQuantityChange,
  onPriceChange,
  onTotalInclChange,
  onAddProductClick,
  loadProductOptions,
  vatPercentage,
  showVatColumn = true,
  showTotalExclColumn = true,
  showTotalInclColumn = true,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'visible',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5 
              }}>
                {t("product_name") || "PRODUCT NAME"}
              </TableCell>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'center', 
                width: '120px' 
              }}>
                {t("quantity") || "QUANTITY"}
              </TableCell>
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'right', 
                width: '120px' 
              }}>
                {t("price") || "PRICE"}
              </TableCell>
              {showVatColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '100px' 
                }}>
                  {t("vat") || "VAT"}
                </TableCell>
              )}
              {showTotalExclColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '130px' 
                }}>
                  {t("total_excl") || "TOTAL EXCL."}
                </TableCell>
              )}
              {showTotalInclColumn && (
                <TableCell sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px', 
                  py: 1.5, 
                  textAlign: 'right', 
                  width: '130px' 
                }}>
                  {t("total_incl") || "TOTAL INCL."}
                </TableCell>
              )}
              <TableCell sx={{ 
                color: 'white', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                fontSize: '0.75rem', 
                letterSpacing: '0.5px', 
                py: 1.5, 
                textAlign: 'center', 
                width: '100px' 
              }}>
                {t("actions") || "ACTIONS"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Added Products Rows */}
            {products.map((p) => {
              const vatPerUnit = (parseFloat(p.VATAmount || 0) / (p.Quantity || 1)).toFixed(2);
              const totalExcl = parseFloat(p.TotalExcl || 0);
              const totalIncl = parseFloat(p.TotalIncl || 0);
              
              return (
                <TableRow 
                  key={p._rowKey || p.Id}
                  sx={{
                    '&:hover': { bgcolor: '#f8f9fa' },
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <TableCell sx={{ py: 1.5, fontSize: '0.85rem', color: '#333' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {p.Name || p.ProductName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                    <TextField
                      type="number"
                      value={p.Quantity}
                      onChange={(e) => onQuantityChange && onQuantityChange(p, parseFloat(e.target.value) || 0)}
                      size="small"
                      inputProps={{ min: 1, step: 1 }}
                      sx={{
                        width: '80px',
                        '& .MuiOutlinedInput-root': {
                          '& input': { textAlign: 'center', py: 0.5 }
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                    <TextField
                      type="number"
                      value={p.Price}
                      onChange={(e) => onPriceChange && onPriceChange(p, parseFloat(e.target.value) || 0)}
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{
                        width: '100px',
                        '& .MuiOutlinedInput-root': {
                          '& input': { textAlign: 'right', py: 0.5, fontWeight: 500 }
                        }
                      }}
                    />
                  </TableCell>
                  {showVatColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 500, color: '#f57c00' }}>
                      €{vatPerUnit}
                    </TableCell>
                  )}
                  {showTotalExclColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 600, color: '#388e3c' }}>
                      €{totalExcl.toFixed(2)}
                    </TableCell>
                  )}
                  {showTotalInclColumn && (
                    <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                      <TextField
                        type="number"
                        value={totalIncl.toFixed(2)}
                        onChange={(e) => onTotalInclChange && onTotalInclChange(p, parseFloat(e.target.value) || 0)}
                        size="small"
                        sx={{
                          width: '90px',
                          '& .MuiOutlinedInput-root': {
                            '& input': { textAlign: 'right', py: 0.5, fontWeight: 600 }
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => onProductRemove && onProductRemove(p)}
                      sx={{
                        color: '#d32f2f',
                        '&:hover': { bgcolor: alpha('#d32f2f', 0.1) }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            
            {/* Product Search Row - For adding new products */}
            <TableRow sx={{ bgcolor: '#fffef0', borderTop: '2px solid #667eea' }}>
              <TableCell colSpan={7} sx={{ py: 2, position: 'relative', zIndex: 10 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {onAddProductClick && (
                    <IconButton
                      size="small"
                      onClick={onAddProductClick}
                      sx={{
                        color: '#667eea',
                        bgcolor: alpha('#667eea', 0.1),
                        '&:hover': { bgcolor: alpha('#667eea', 0.2) }
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Box sx={{ flex: 1, position: 'relative', zIndex: 20 }}>
                    <ProductSelect
                      value={null}
                      onChange={(selectedOption) => {
                        if (selectedOption && onProductAdd) {
                          const productPrice = selectedOption.SalePrice || selectedOption.Price || selectedOption.price || "";
                          onProductAdd(selectedOption, 1, productPrice);
                        }
                      }}
                      loadOptions={loadProductOptions}
                      placeholder={t("search_products") || "Search and add products..."}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
            
            {/* Totals Row - At the bottom */}
            {products.length > 0 && (
              <TableRow sx={{ bgcolor: '#f0f0f0', borderTop: '2px solid #667eea' }}>
                <TableCell sx={{ py: 1.5, fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                  {t("total") || "TOTAL"}
                </TableCell>
                <TableCell sx={{ py: 1.5, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                  {products.reduce((sum, p) => sum + parseFloat(p.Quantity || 0), 0)}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}></TableCell>
                {showVatColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#f57c00' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.VATAmount || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                {showTotalExclColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#388e3c' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.TotalExcl || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                {showTotalInclColumn && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right', fontWeight: 700, fontSize: '0.95rem', color: '#667eea' }}>
                    €{products.reduce((sum, p) => sum + parseFloat(p.TotalIncl || 0), 0).toFixed(2)}
                  </TableCell>
                )}
                <TableCell sx={{ py: 1.5 }}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default DocumentProductsTable;
